import { useState, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonSave,
    SttButtonDelete,
    SttFormDate,
    SttFormPrice,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttSelectFixedInterval,
    SttFormCheck,
    SelectGroupField,
} from '../../../sporttia/all';
import HandlePaymentsModal from './HandlePaymentsModal';
import { getErrorMessage, parsePrice, subsetObject } from '../../../lib/utils';
import { useConfirmDialog } from '../../../lib/hooks';
import GroupDiscountsList from './GroupDiscountsList';
import constants from '../../../config/constants';
import type { Group } from '../../../types/models/Group';
import { PERMISSIONS } from '../../../lib/permissions';
import {
    MAX_FEE_GRACE_DAYS,
    parseFeeGraceDays,
    sanitizeFeeGraceDays,
} from './feeGraceDays';

// Mientras se edita, los días de gracia son el texto del campo.
type GroupForm = Omit<Group, 'feeGraceDays'> & {
    feeGraceDays?: number | string | null;
};

type Props = {
    item: Group;
    onSave?: (group: Group) => void;
    onDelete?: (group: Group) => void;
};

/**
 * GroupDetails: component to visualize and save the group's details. Sports groups cannot be modified.
 * Object item: group to view details from
 * <Object>array parents: list of possible parent groups (based on year)
 */
export default function GroupDetails({
    item,
    onSave,
    onDelete,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [openConfirmDialog, , confirmDialog] = useConfirmDialog();
    const [loading, setLoading] = useState<boolean>(false);

    const [groupForm, setGroupForm] = useState<GroupForm>(item);

    const shouldUpdateDates = groupForm.end !== item.end;

    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (name) {
            setGroupForm((prev: any) => ({ ...prev, [name]: value }));
        }
    }

    /**
     * Días de gracia listos para enviar, o undefined (tras avisar) si el valor
     * no es válido. Se lee antes de preguntar por las fechas de los usuarios.
     */
    function readFeeGraceDays(): number | null | undefined {
        const feeGraceDays = parseFeeGraceDays(groupForm.feeGraceDays);
        if (feeGraceDays === undefined) {
            cxt.showMessage(
                'W',
                t('page.sc.group.feeGraceDaysInvalid', {
                    max: MAX_FEE_GRACE_DAYS,
                }),
            );
        }
        return feeGraceDays;
    }

    function saveGroup(feeGraceDays: number | null, updateUserDates?: boolean) {
        const fields = `name,description,${
            groupForm.type === constants.group.types.family.name
                ? `maxBeneficiaries`
                : `maxMembers`
        },idParent,ini,end,enrollmentPrice,feePrice,feeInterval,purchaseOnline,chargeFirstFeeOnSignup,expiration,feeTaxConceptCode,signupTaxConceptCode`;

        const params: Record<string, unknown> = subsetObject(groupForm, fields);
        params.enrollmentPrice = parsePrice(params.enrollmentPrice as string);
        params.feePrice = parsePrice(params.feePrice as string);
        // Solo se envía si ha cambiado. Vacío viaja como null, que es lo que
        // quita los días de gracia.
        if (feeGraceDays !== (item.feeGraceDays ?? null)) {
            params.feeGraceDays = feeGraceDays;
        }
        params.idMandatoryGroup = groupForm.mandatoryGroup?.id
            ? groupForm.mandatoryGroup.id
            : '';
        params.idParent = groupForm.parent?.id ?? '';
        if (params.feeInterval === '0000-00-00 00:00:00') {
            params.feeInterval = '';
        }
        if (updateUserDates) {
            params.updateUserPeriod = true;
        }

        if (params.purchaseOnline === null) {
            params.purchaseOnline = false;
        }

        if (
            'chargeFirstFeeOnSignup' in params &&
            params.chargeFirstFeeOnSignup === null
        ) {
            params.chargeFirstFeeOnSignup = false;
        }

        setLoading(true);
        cxt?.api('PUT', `/groups/${item.id}`, {
            params,
            success: (response: { group?: Group }) => {
                if (response.group) {
                    cxt?.showMessage('S', t('generic.saved'));
                    onSave?.(response.group);
                }
                setLoading(false);
            },
            error: (err: any) => {
                cxt.showMessage('E', getErrorMessage(err));
                setLoading(false);
            },
        });
    }

    function deleteGroup(mode?: string) {
        // Without a 'mode' specified, we ask the user for confirmation before nuking the group.
        cxt?.api('DELETE', `/groups/${item.id}`, {
            confirmation: mode === undefined,
            params: mode
                ? {
                      paymentProcess: mode,
                  }
                : {},
            success: (response: { group?: Group }) => {
                if (response.group) {
                    if (typeof onDelete === 'function') {
                        onDelete(response.group);
                    }
                }
            },
        });
    }

    return (
        <>
            <Grid container spacing={3}>
                {/* Left side - Form fields */}
                <Grid
                    container
                    size={item.trash ? 12 : { md: 7, sm: 7, xs: 12 }}
                >
                    <Grid spacing={3} container>
                        {/* --- ID (read-only) --- */}
                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            <SttFormText
                                caption="ID"
                                defVal={item.id?.toString()}
                                disabled
                            />
                        </Grid>

                        {/* --- Created date (read-only) --- */}
                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            <SttFormDate
                                caption={t('generic.created')}
                                defVal={item.created}
                                disabled
                            />
                        </Grid>

                        <Grid size={12}>
                            <SttFormText
                                name="name"
                                caption={t('generic.name')}
                                defVal={groupForm.name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            {groupForm.type !==
                            constants.group.types.family.name ? (
                                <SttFormText
                                    name="maxMembers"
                                    caption={t('MaxUsers')}
                                    defVal={
                                        groupForm.maxMembers?.toString() ??
                                        undefined
                                    }
                                    onChange={handleChange}
                                />
                            ) : (
                                <SttFormText
                                    name="maxBeneficiaries"
                                    caption={t('generic.beneficiaries')}
                                    defVal={
                                        groupForm.maxBeneficiaries?.toString() ??
                                        undefined
                                    }
                                    onChange={handleChange}
                                />
                            )}
                        </Grid>

                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            <SttFormSelect
                                name="type"
                                caption={t('generic.type')}
                                disabled
                                defVal={groupForm.type}
                                options={[
                                    {
                                        caption: t(
                                            constants.group.types.normal
                                                .translationCode,
                                        ),
                                        value: constants.group.types.normal
                                            .name,
                                    },
                                    {
                                        caption: t(
                                            constants.group.types.abonados
                                                .translationCode,
                                        ),
                                        value: constants.group.types.abonados
                                            .name,
                                    },
                                    {
                                        caption: t(
                                            constants.group.types.sport
                                                .translationCode,
                                        ),
                                        value: constants.group.types.sport.name,
                                    },
                                    {
                                        caption: t(
                                            constants.group.types.family
                                                .translationCode,
                                        ),
                                        value: constants.group.types.family
                                            .name,
                                    },
                                ]}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={12}>
                            <SelectGroupField
                                name="parent"
                                caption={t('ParentGroup')}
                                defVal={
                                    groupForm.parent
                                        ? {
                                              id: groupForm.parent.id!,
                                              name: groupForm.parent.name!,
                                          }
                                        : null
                                }
                                onChange={handleChange}
                                excludeGroupId={item.id}
                                types="NORMAL,ABONADOS"
                            />
                        </Grid>

                        <Grid size={12}>
                            <SttFormTextarea
                                name="description"
                                caption={t('generic.description')}
                                defVal={groupForm.description}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* --- Date ini --- */}
                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SttFormDate
                                name="ini"
                                caption={t('PeriodFrom')}
                                defVal={groupForm.ini}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* --- Date end --- */}
                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SttFormDate
                                name="end"
                                caption={t('Until')}
                                defVal={groupForm.end}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* --- Mandatory group --- */}
                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SelectGroupField
                                name="mandatoryGroup"
                                caption={t('generic.mandatoryGroup')}
                                defVal={
                                    groupForm.mandatoryGroup
                                        ? {
                                              id: groupForm.mandatoryGroup.id!,
                                              name:
                                                  groupForm.mandatoryGroup
                                                      .name ?? '',
                                          }
                                        : undefined
                                }
                                onChange={handleChange}
                                excludeGroupId={item.id}
                            />
                        </Grid>

                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SttSelectFixedInterval
                                caption={t('generateFeesEvery')}
                                name="feeInterval"
                                defVal={
                                    groupForm.feeInterval ??
                                    '0000-00-00 00:00:00'
                                }
                                options={[
                                    'NO PERIOD',
                                    '1 WEEK',
                                    '2 WEEK',
                                    '1 MONTH',
                                    '2 MONTH',
                                    '3 MONTH',
                                    '4 MONTH',
                                    '6 MONTH',
                                    '1 YEAR',
                                ]}
                                onChange={handleChange}
                                required={
                                    parseFloat(
                                        String(groupForm.feePrice ?? 0).replace(
                                            ',',
                                            '.',
                                        ),
                                    ) > 0
                                }
                            />
                        </Grid>

                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SttFormPrice
                                name="feePrice"
                                caption={t('feePrice')}
                                defVal={groupForm.feePrice?.toString() ?? ''}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* --- Fee grace days --- */}
                        <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                            <SttFormText
                                name="feeGraceDays"
                                caption={t('page.sc.group.feeGraceDays')}
                                help={t('page.sc.group.feeGraceDaysHelp')}
                                defVal={
                                    groupForm.feeGraceDays?.toString() ?? ''
                                }
                                onChange={({ name, value }) =>
                                    handleChange({
                                        name,
                                        value: sanitizeFeeGraceDays(value),
                                    })
                                }
                                slotProps={{
                                    htmlInput: { inputMode: 'numeric' },
                                }}
                            />
                        </Grid>

                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            <SttFormPrice
                                name="enrollmentPrice"
                                caption={t('generic.inscription')}
                                defVal={
                                    groupForm.enrollmentPrice?.toString() ?? ''
                                }
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                            <SttSelectFixedInterval
                                caption={t('generic.expiration')}
                                name="expiration"
                                defVal={groupForm.expiration ?? ''}
                                options={['!FORTNIGHT']}
                                onChange={handleChange}
                            />
                        </Grid>

                        {Object.values(constants.orgt.scIds).includes(
                            cxt?.sc?.id as 3444 | 2401,
                        ) && (
                            <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                                <SttFormSelect
                                    name="feeTaxConceptCode"
                                    caption={t('orgt.feeTaxConcept')}
                                    defVal={
                                        groupForm?.feeTaxConceptCode ??
                                        constants.orgt.taxCodes.noOption
                                    }
                                    options={[
                                        {
                                            caption: t('orgt.noOption'),
                                            value: null,
                                        },
                                        {
                                            caption: t('orgt.feePass'),
                                            value: constants.orgt.taxCodes
                                                .feePassCode,
                                        },
                                        {
                                            caption: t('orgt.feeRoomActivity'),
                                            value: constants.orgt.taxCodes
                                                .feeRoomActivity,
                                        },
                                        {
                                            caption: t(
                                                'orgt.feeAquaticActivity',
                                            ),
                                            value: constants.orgt.taxCodes
                                                .feeAquaticActivity,
                                        },
                                        {
                                            caption: t('orgt.athleticismFee'),
                                            value: constants.orgt.taxCodes
                                                .athleticismFee,
                                        },
                                        {
                                            caption: t('orgt.summerFees'),
                                            value: constants.orgt.taxCodes
                                                .summerFees,
                                        },
                                        {
                                            caption: t('orgt.facilityRental'),
                                            value: constants.orgt.taxCodes
                                                .facilityRental,
                                        },
                                    ]}
                                    onChange={handleChange}
                                />
                            </Grid>
                        )}

                        {Object.values(constants.orgt.scIds).includes(
                            cxt?.sc?.id as 3444 | 2401,
                        ) && (
                            <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                                <SttFormSelect
                                    name="signupTaxConceptCode"
                                    caption={t('orgt.signupTaxConcept')}
                                    defVal={
                                        groupForm?.signupTaxConceptCode ??
                                        constants.orgt.taxCodes.noOption
                                    }
                                    options={[
                                        {
                                            caption: t('orgt.noOption'),
                                            value: null,
                                        },
                                        {
                                            caption: t('orgt.enrollment'),
                                            value: constants.orgt.taxCodes
                                                .enrollment,
                                        },
                                    ]}
                                    onChange={handleChange}
                                />
                            </Grid>
                        )}

                        {/* --- Purchase online --- */}
                        {groupForm.type ===
                            constants.group.types.abonados.name && (
                            <Grid size={12}>
                                <SttFormCheck
                                    name="purchaseOnline"
                                    caption={t('page.sc.bono.onlinePurchase')}
                                    checked={groupForm.purchaseOnline ?? false}
                                    onChange={handleChange}
                                />
                            </Grid>
                        )}

                        {/* --- Charge first fee on signup --- */}
                        {groupForm.type ===
                            constants.group.types.abonados.name &&
                            (groupForm.purchaseOnline ?? false) && (
                                <Grid size={12}>
                                    <SttFormCheck
                                        name="chargeFirstFeeOnSignup"
                                        caption={t(
                                            'page.sc.group.chargeFirstFeeOnSignup',
                                        )}
                                        checked={
                                            groupForm.chargeFirstFeeOnSignup ??
                                            false
                                        }
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}
                    </Grid>
                </Grid>

                {/* Right side - Discounts list */}
                {!item.trash && (
                    <Grid container size={{ sm: 5, xs: 12 }}>
                        <Grid size={12}>
                            <GroupDiscountsList groupId={item.id} />
                        </Grid>
                    </Grid>
                )}
            </Grid>

            {/* --- Buttons --- */}
            <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                {!item.trash && (
                    <SttButtonDelete
                        onClick={() => {
                            if (
                                item.type === constants.group.types.sport.name
                            ) {
                                deleteGroup();
                            } else {
                                setDeleteModal(true);
                            }
                        }}
                        permission={PERMISSIONS.GROUP.DELETE}
                    />
                )}

                {groupForm.type !== constants.group.types.sport.name &&
                    !item.trash && (
                        <SttButtonSave
                            loading={loading}
                            permission={PERMISSIONS.GROUP.MODIFY}
                            onClick={() => {
                                const feeGraceDays = readFeeGraceDays();
                                if (feeGraceDays === undefined) {
                                    return;
                                }

                                if (shouldUpdateDates) {
                                    openConfirmDialog({
                                        title: `${t('generic.modify')} ${t('generic.date')}`,
                                        content: t(
                                            'page.groups.modifyUserDates',
                                        ),
                                        onConfirm: () =>
                                            saveGroup(feeGraceDays, true),
                                        onCancel: () => saveGroup(feeGraceDays),
                                    });
                                } else {
                                    saveGroup(feeGraceDays);
                                }
                            }}
                        />
                    )}
            </Box>

            {/* --- Modal asking before deleting --- */}
            {deleteModal && (
                <HandlePaymentsModal
                    warningText={t('sc.groups.delete.howToHandlePayments')}
                    open={deleteModal}
                    onClose={() => setDeleteModal(false)}
                    onAccept={(mode) => deleteGroup(mode || undefined)}
                />
            )}

            {confirmDialog}
        </>
    );
}
